import { computed, inject, provide, ref, watch } from 'vue';

// A CONVERSA QUE CHEGA NO TOPO (24/10/2026). Quando uma conversa recebe mensagem, ela salta
// para o topo da lista e as outras pulam de lugar — a pessoa perde onde estava lendo e não vê
// o que mudou. Aqui a lista compara a ordem de antes com a de agora e marca, por ~2,6 s, a
// conversa que SUBIU para o topo ou ENTROU no topo. O cartão desliza e acende um anel.
//
// Só dentro da MESMA aba e filtro: trocar de aba, de status ou de etiqueta muda a lista
// inteira sem que nada tenha chegado. Página carregada ao rolar entra no FIM e não conta.
// A lista é virtualizada (virtua): o trajeto inteiro não se anima, só a chegada no topo.
const CHAVE = Symbol('chegadasNaLista');
const TOPO = 3;
const TEMPO = 2600;
// A CONVERSA QUE SAI (24/10/2026): resolvida, transferida ou fora do filtro, ela sumia e as de
// baixo pulavam. Fica na lista por SAIDA ms marcada como "saindo" — esmaece e recolhe a altura
// — e as de baixo sobem deslizando (a lista virtualizada remede a altura a cada quadro). Mais
// de MAX_SAIDAS de uma vez (ação em massa) saem direto: uma cascata de vinte não ajuda ninguém.
const SAIDA = 300; // = --mov-3, a duração da animação de saída do cartão
const MAX_SAIDAS = 5;

export function criarChegadasNaLista(lista, chaveDoFiltro) {
  const chegaram = ref(new Set());
  const saindo = ref(new Map()); // id → { conversa, indice }
  let anterior = null;
  let anteriores = [];
  let chaveAnterior = null;

  const segurarSaidas = (idsAgora, mesmaLista) => {
    if (!mesmaLista) {
      saindo.value = new Map();
      return;
    }
    const ficam = new Set(idsAgora);
    const sairam = anteriores
      .map((conversa, indice) => ({ conversa, indice }))
      .filter(({ conversa }) => !ficam.has(conversa.id));
    if (!sairam.length || sairam.length > MAX_SAIDAS) return;
    const mapa = new Map(saindo.value);
    sairam.forEach(item => mapa.set(item.conversa.id, item));
    saindo.value = mapa;
    setTimeout(() => {
      const resto = new Map(saindo.value);
      sairam.forEach(({ conversa }) => resto.delete(conversa.id));
      saindo.value = resto;
    }, SAIDA);
  };

  const marcar = ids => {
    if (!ids.length) return;
    chegaram.value = new Set([...chegaram.value, ...ids]);
    setTimeout(() => {
      const resto = new Set(chegaram.value);
      ids.forEach(id => resto.delete(id));
      chegaram.value = resto;
    }, TEMPO);
  };

  watch(
    () => [lista.value.map(c => c.id), chaveDoFiltro.value],
    ([ids, chave]) => {
      const mesmaLista = chave === chaveAnterior && anterior && anterior.length;
      if (mesmaLista) {
        const posicao = new Map(anterior.map((id, i) => [id, i]));
        const novas = ids
          .slice(0, TOPO)
          .filter(id => !posicao.has(id) || posicao.get(id) >= TOPO);
        marcar(novas);
      }
      segurarSaidas(ids, !!mesmaLista);
      anterior = ids;
      anteriores = lista.value.slice();
      chaveAnterior = chave;
    },
    // A lista que já está na tela ao montar é a base, não novidade.
    { immediate: true }
  );

  // O que a lista virtualizada desenha: a lista de verdade com as que estão saindo de volta
  // no lugar onde estavam, até terminarem de sair.
  const listaComSaidas = computed(() => {
    if (!saindo.value.size) return lista.value;
    const ids = new Set(lista.value.map(c => c.id));
    const resultado = lista.value.slice();
    [...saindo.value.values()]
      .filter(({ conversa }) => !ids.has(conversa.id))
      .sort((a, b) => a.indice - b.indice)
      .forEach(({ conversa, indice }) => {
        resultado.splice(Math.min(indice, resultado.length), 0, conversa);
      });
    return resultado;
  });

  provide(CHAVE, { chegaram, saindo });
  return { chegaram, listaComSaidas };
}

// No cartão: `chegouAgora(id)`. Fora da lista (sem quem forneça), nada chega.
export function useChegadasNaLista() {
  const estado = inject(CHAVE, null);
  return {
    chegouAgora: id => !!estado?.chegaram.value.has(id),
    saindoAgora: id => !!estado?.saindo.value.has(id),
  };
}
